/**
 * Module: StatsScreen
 * Role: Overlay-page at /stats — hero "Заработано всего" + 5-month bar chart +
 *       "Среднее задание" / "Принято %" cards. Data composed from 3 endpoints.
 *
 * Backend contracts:
 *   - GET /api/v2/buyer/earnings/balance              (TECH-1010 Task 1)
 *   - GET /api/v2/buyer/earnings/history?limit=100   (limit capped at 100 — backend max)
 *   - GET /api/v2/buyer/profile/stats                 (TECH-1010 Task 3)
 *
 * Shared cache keys (one fetch across screens):
 *   - ['earnings','balance']        — shared with WalletScreen (IA-1)
 *   - ['buyer','profile','stats']   — shared with ProfileScreen (IA-2, FTR-1032)
 *   - ['earnings','history','stats']— DISTINCT from WalletScreen ['earnings','history']
 *                                     (different limit; prevents cache collision)
 *
 * Uses:
 *   - atoms (IOSScreen, PageHeader, Card, EmptyState, Skeleton)
 *   - @tanstack/react-query (useQuery)
 *   - api/client (apiFetch)
 *   - hooks/usePlatformBack (TG BackButton)
 *   - lib/format (formatKopecks)
 *   - lib/buyerStats (computeAcceptancePercent, aggregateByMonth, currentMonthCredits)
 *
 * Used by:
 *   - App.tsx (route /stats, sibling of AppShell)
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { IOSScreen, PageHeader, Card, EmptyState, Skeleton } from '../components/atoms';
import { apiFetch } from '../api/client';
import { usePlatformBack } from '../hooks/usePlatformBack';
import { P, S, T } from '../styles/tokens';
import { formatKopecks } from '../lib/format';
import {
  computeAcceptancePercent,
  aggregateByMonth,
  currentMonthCredits,
  type HistoryItem,
} from '../lib/buyerStats';

// ─── Backend contracts (L-240 inline) ────────────────────────────────────────
interface BalanceView {
  available_kopecks: number;
  pending_kopecks: number;
  accrued_total_kopecks: number;
  withdrawn_total_kopecks: number;
  currency: 'RUB';
  calculated_at: string;
}
interface EarningsHistoryResponse {
  items: HistoryItem[];
  pagination: { total: number; limit: number; offset: number; has_next: boolean };
}
interface ProfileStatsView {
  completed_slots_count: number;
  approved_count: number;
  rejected_count: number;
  sla_cancelled_count: number;
}

const MONTHS_BACK = 5;

// ─── Bar inactive color (prototype uses '#E8E3D4') ───────────────────────────
const BAR_INACTIVE = '#E8E3D4';

export default function StatsScreen() {
  const navigate = useNavigate();
  const onBack = () => navigate(-1);
  usePlatformBack();

  const balanceQ = useQuery<BalanceView>({
    queryKey: ['earnings', 'balance'],
    queryFn: () => apiFetch<BalanceView>('/api/v2/buyer/earnings/balance'),
    retry: false,
  });
  const historyQ = useQuery<EarningsHistoryResponse>({
    queryKey: ['earnings', 'history', 'stats'],
    queryFn: () => apiFetch<EarningsHistoryResponse>('/api/v2/buyer/earnings/history?limit=100'),
    retry: false,
  });
  const statsQ = useQuery<ProfileStatsView>({
    queryKey: ['buyer', 'profile', 'stats'],
    queryFn: () => apiFetch<ProfileStatsView>('/api/v2/buyer/profile/stats'),
    retry: false,
  });

  const isPending = balanceQ.isPending || historyQ.isPending || statsQ.isPending;
  const balanceErr = balanceQ.isError;

  const items: HistoryItem[] = useMemo(
    () => historyQ.data?.items ?? [],
    [historyQ.data],
  );
  const buckets = useMemo(() => aggregateByMonth(items, MONTHS_BACK), [items]);
  const maxKopecks = Math.max(1, ...buckets.map((b) => b.kopecks));
  const monthCredits = useMemo(() => currentMonthCredits(items), [items]);

  // Average task: avg of all credit amounts in full history window
  const avgKopecks = useMemo(() => {
    const credits = items.filter((it) => it.type === 'credit');
    if (credits.length === 0) return null;
    const sum = credits.reduce((acc, it) => acc + it.amount_kopecks, 0);
    return Math.round(sum / credits.length);
  }, [items]);

  const acceptance = statsQ.data ? computeAcceptancePercent(statsQ.data) : null;
  const hasAnyHistory = items.some((it) => it.type === 'credit');

  return (
    <IOSScreen tabBarSpace={false}>
      <PageHeader title="Статистика" subtitle="Сколько и за что" onBack={onBack} />

      {isPending && <StatsSkeleton />}

      {!isPending && balanceErr && (
        <EmptyState title="Не удалось загрузить" subtitle="Попробуй позже" />
      )}

      {!isPending && !balanceErr && (
        <div style={{ padding: '14px 16px 0' }}>
          {/* Hero card — earnings total + bar chart */}
          <Card p={16} r={20}>
            {/* Label — uppercase caption, secondary */}
            <div
              style={{
                font: T.footnote,
                fontWeight: 600,
                color: S.secondaryLabel,
                textTransform: 'uppercase',
                letterSpacing: -0.08,
              }}
            >
              Заработано всего
            </div>

            {/* Hero amount — 32px serif, prototype: T.title1 at 32px override */}
            <div
              style={{
                fontFamily: T.fontDisplay,
                fontSize: 32,
                fontWeight: 700,
                lineHeight: '38px',
                letterSpacing: -0.3,
                color: S.label,
                marginTop: 4,
              }}
            >
              {formatKopecks(balanceQ.data?.accrued_total_kopecks ?? 0)}
            </div>

            {/* This-month accent — Caveat handwriting annotation */}
            {monthCredits > 0 && (
              <div
                style={{
                  fontFamily: T.fontAccent,
                  fontSize: 17,
                  fontWeight: 700,
                  color: P.success,
                  marginTop: 4,
                  letterSpacing: 0,
                }}
              >
                +{formatKopecks(monthCredits)} в этом месяце
              </div>
            )}

            {/* Bar chart — 5 months */}
            <div
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'flex-end',
                marginTop: 16,
                height: 100,
              }}
            >
              {buckets.map((b) => (
                <div
                  key={b.label}
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <div style={{ flex: 1, width: '100%', display: 'flex', alignItems: 'flex-end' }}>
                    <div
                      data-bar-current={b.isCurrent ? 'true' : undefined}
                      style={{
                        width: '100%',
                        height: `${Math.max(b.kopecks > 0 ? 4 : 0, (b.kopecks / maxKopecks) * 100)}%`,
                        background: b.isCurrent ? P.terracotta : BAR_INACTIVE,
                        borderRadius: '6px 6px 2px 2px',
                        transition: 'height 0.5s cubic-bezier(0.32, 0.72, 0, 1)',
                        minHeight: b.kopecks > 0 ? 4 : 0,
                      }}
                    />
                  </div>
                  {/* Month label — 11px body matches prototype T.caption2 */}
                  <div
                    style={{
                      font: T.label,
                      color: b.isCurrent ? S.label : S.secondaryLabel,
                      fontWeight: b.isCurrent ? 700 : 600,
                    }}
                  >
                    {b.label}
                  </div>
                </div>
              ))}
            </div>

            {!hasAnyHistory && (
              <div
                style={{
                  font: T.footnote,
                  color: S.secondaryLabel,
                  marginTop: 12,
                  textAlign: 'center',
                }}
              >
                Данных пока нет
              </div>
            )}
          </Card>

          {/* Stats mini-cards row */}
          <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
            {/* Average task amount */}
            <Card p={14} r={20} style={{ flex: 1 }}>
              <div
                style={{
                  font: T.footnote,
                  fontWeight: 600,
                  color: S.secondaryLabel,
                  textTransform: 'uppercase',
                  letterSpacing: -0.08,
                }}
              >
                Среднее задание
              </div>
              <div
                style={{
                  fontFamily: T.fontDisplay,
                  fontSize: 22,
                  fontWeight: 700,
                  lineHeight: '28px',
                  letterSpacing: -0.2,
                  color: S.label,
                  marginTop: 4,
                }}
              >
                {avgKopecks !== null ? `+${formatKopecks(avgKopecks)}` : '—'}
              </div>
            </Card>

            {/* Acceptance rate */}
            <Card p={14} r={20} style={{ flex: 1 }}>
              <div
                style={{
                  font: T.footnote,
                  fontWeight: 600,
                  color: S.secondaryLabel,
                  textTransform: 'uppercase',
                  letterSpacing: -0.08,
                }}
              >
                Принято
              </div>
              <div
                style={{
                  fontFamily: T.fontDisplay,
                  fontSize: 22,
                  fontWeight: 700,
                  lineHeight: '28px',
                  letterSpacing: -0.2,
                  color: acceptance !== null ? P.success : S.label,
                  marginTop: 4,
                }}
              >
                {acceptance !== null ? `${acceptance}%` : '—'}
              </div>
            </Card>
          </div>
        </div>
      )}
    </IOSScreen>
  );
}

// ─── Skeleton (hero card + 2 small cards) ────────────────────────────────────
function StatsSkeleton() {
  return (
    <div style={{ padding: '14px 16px 0' }}>
      <Card p={16} r={20}>
        <Skeleton h={14} w={140} style={{ marginBottom: 10 }} />
        <Skeleton h={36} w={180} />
        <Skeleton h={100} style={{ marginTop: 16 }} />
      </Card>
      <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
        <Card p={14} r={20} style={{ flex: 1 }}>
          <Skeleton h={12} w={110} style={{ marginBottom: 8 }} />
          <Skeleton h={22} w={80} />
        </Card>
        <Card p={14} r={20} style={{ flex: 1 }}>
          <Skeleton h={12} w={70} style={{ marginBottom: 8 }} />
          <Skeleton h={22} w={60} />
        </Card>
      </div>
    </div>
  );
}
